"use client";

import type { CTAGroupItem, CTAGroupProps } from "@/components/CTAGroup";
import StickyOfferBanner from ".";
import { defaultStickyOfferBannerData } from "./default";
import type { StickyOfferBannerSlotData } from "./types";

interface BaseStickyOfferBannerProps {
  data?: StickyOfferBannerSlotData;
}

const BaseStickyOfferBanner = ({
  data = defaultStickyOfferBannerData,
}: BaseStickyOfferBannerProps) => {
  const titleCopy = data.title?.copy?.trim();
  const responsiveMobileTitleCopy = data.mobileTitle?.copy?.trim();
  const responsiveDesktopTitleCopy = data.desktopTitle?.copy?.trim();
  const mobileTitleCopy =
    responsiveMobileTitleCopy || titleCopy || responsiveDesktopTitleCopy;
  const desktopTitleCopy =
    responsiveDesktopTitleCopy || titleCopy || responsiveMobileTitleCopy;
  const resolvedTitleCopy = titleCopy || mobileTitleCopy || desktopTitleCopy;
  if (!resolvedTitleCopy) return null;

  const normalizedSubtitle = data.subtitle?.copy?.trim();
  const responsiveMobileSubtitle = data.mobileSubtitle?.copy?.trim();
  const responsiveDesktopSubtitle = data.desktopSubtitle?.copy?.trim();
  const normalizedMobileSubtitle =
    responsiveMobileSubtitle || normalizedSubtitle || responsiveDesktopSubtitle;
  const normalizedDesktopSubtitle =
    responsiveDesktopSubtitle || normalizedSubtitle || responsiveMobileSubtitle;
  const normalizedCtas = (data.ctaGroup?.ctas ?? [])
    .map((cta) => ({
      ...cta,
      copy: cta.copy?.trim() ?? "",
      url: cta.url?.trim(),
      modalKey: cta.modalKey?.trim(),
      tooltipCopy: cta.tooltipCopy?.trim(),
    }))
    .filter((cta) => {
      if (!cta.copy) return false;
      if (cta.action === "tooltip" && !cta.tooltipCopy) return false;
      return true;
    });

  const resolvedCtaGroup: CTAGroupProps | undefined = normalizedCtas.length
    ? {
        ...data.ctaGroup,
        ctas: normalizedCtas as CTAGroupItem[],
      }
    : undefined;

  return (
    <StickyOfferBanner
      elementName={data.elementName}
      icon={data.icon?.assetKey ? data.icon : undefined}
      title={titleCopy ? { copy: titleCopy } : undefined}
      mobileTitle={mobileTitleCopy ? { copy: mobileTitleCopy } : undefined}
      desktopTitle={desktopTitleCopy ? { copy: desktopTitleCopy } : undefined}
      subtitle={normalizedSubtitle ? { copy: normalizedSubtitle } : undefined}
      mobileSubtitle={
        normalizedMobileSubtitle
          ? { copy: normalizedMobileSubtitle }
          : undefined
      }
      desktopSubtitle={
        normalizedDesktopSubtitle
          ? { copy: normalizedDesktopSubtitle }
          : undefined
      }
      ctaGroup={resolvedCtaGroup}
      hasInlineTextLink={data.hasInlineTextLink}
      backgroundColor={data.backgroundColor}
      contentAlignment={data.contentAlignment}
    />
  );
};

export default BaseStickyOfferBanner;
